import { useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import axios from 'axios';
import Swal from 'sweetalert2';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

export default function PropertyDetail() {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isPaying, setIsPaying] = useState(false);
  const isLoggedIn = !!localStorage.getItem('token'); // Gunakan token untuk cek login backend

  // State Buyer dengan sinkronisasi field database
  const [buyer, setBuyer] = useState({
    nama_lengkap: localStorage.getItem('usernamePi') || '',
    telepon: '',
    alamat: '',
    provinsi: '',
    kabupaten: '',
    kecamatan: '',
    desa: '',
    kode_pos: '',
  });

  useEffect(() => {
    // Handler untuk pembayaran yang belum selesai (Incomplete Payments)
    if (window?.Pi?.onIncompletePaymentFound) {
      window.Pi.onIncompletePaymentFound((payment) => {
        console.log('🔄 Pembayaran tertunda ditemukan:', payment);
        // Anda bisa menambahkan logika sinkronisasi manual di sini jika perlu
      });
    }
  }, []);

  useEffect(() => {
    // Fetch detail properti
    axios
      .get(`https://prodmejatika.bljambi.com/api/produk-publik/${uuid}`)
      .then((res) => {
        const data = res.data?.data || {};
        
        // Helper untuk memperbaiki path gambar/video
        const fixImagePath = (path) => {
          if (!path || typeof path !== 'string') return null;
          const sanitized = path.replace(/^\/?products\//, 'public/products/');
          return sanitized.startsWith('http') ? sanitized : `https://prodmejatika.bljambi.com/${sanitized}`;
        };

        const slides = [];
        if (Array.isArray(data.photos)) {
          data.photos.forEach((p) => {
            const fixed = fixImagePath(p);
            if (fixed) slides.push({ type: 'image', url: fixed });
          });
        }
        if (data.video_url) {
          const video = fixImagePath(data.video_url);
          if (video) slides.push({ type: 'video', url: video });
        }

        setProperty({ ...data, slides });
        window.scrollTo(0, 0);
      })
      .catch((err) => console.error('❌ Gagal ambil detail:', err));
  }, [uuid]);

  const startPiPayment = async () => {
    setIsPaying(true);
    const accessToken = localStorage.getItem('token');
    const usernamePi = localStorage.getItem('usernamePi');

    if (!window?.Pi) {
      setIsPaying(false);
      return Swal.fire({ 
        icon: 'warning', 
        title: 'Gunakan Pi Browser', 
        text: 'Fitur pembayaran Pi hanya tersedia di dalam Pi Browser.' 
      });
    }

    // Validasi Form sebelum proses Pi
    const required = ['nama_lengkap', 'telepon', 'alamat', 'provinsi', 'kabupaten', 'kecamatan', 'desa', 'kode_pos'];
    const missing = required.find((f) => !buyer[f]?.trim());
    if (missing) {
      setIsPaying(false);
      return Swal.fire({ 
        icon: 'warning', 
        title: 'Data Belum Lengkap', 
        text: `Harap isi bidang ${missing.replace('_', ' ')} terlebih dahulu.` 
      });
    }

    const config = { headers: { Authorization: `Bearer ${accessToken}` } };

    const paymentData = {
      amount: Number(property.price),
      memo: `Beli Properti: ${property.title}`,
      metadata: { 
        productId: property.uuid, 
        buyer_name: buyer.nama_lengkap 
      },
    };

    const callbacks = {
      onReadyForServerApproval: async (paymentId) => {
        try {
          await axios.post('https://prodmejatika.bljambi.com/api/transactions/approve', { paymentId }, config);
        } catch (err) { 
          console.error('Approve failed:', err); 
        }
      },
      onReadyForServerCompletion: async (paymentId, txid) => {
        try {
          // ✅ SYNC: Mengirim data ke tabel 'buyers' & update transaksi
          await axios.post('https://prodmejatika.bljambi.com/api/transactions/complete', {
            paymentId,
            txid,
            produk_uuid: property.uuid, // Key sesuai Controller Laravel
            buyer_data: { 
              ...buyer, 
              user_phi_id: usernamePi 
            }
          }, config);

          Swal.fire({ 
            icon: 'success', 
            title: 'Pembayaran Berhasil', 
            text: 'Terima kasih! Pembayaran Anda telah dikonfirmasi dan data pembeli telah disimpan.' 
          });
          
          setShowModal(false);
          // Reset form setelah sukses
          setBuyer(prev => ({ ...prev, telepon: '', alamat: '' })); 

        } catch (err) {
          console.error('Database sync error:', err);
          Swal.fire({ 
            icon: 'error', 
            title: 'Gagal Simpan Data', 
            text: 'Pembayaran Pi sukses di Blockchain, namun gagal lapor ke sistem database. Harap screenshot bukti transaksi Anda.' 
          });
        }
      },
      onCancel: (paymentId) => {
        console.log('Payment canceled:', paymentId);
        setIsPaying(false);
      },
      onError: (error) => {
        Swal.fire({ icon: 'error', title: 'Transaksi Gagal', text: error.message });
        setIsPaying(false);
      },
    };

    try {
      await window.Pi.createPayment(paymentData, callbacks);
    } catch (err) {
      console.error(err);
      setIsPaying(false);
    }
  };

  if (!property) return <Layout><div className="pt-6 text-center text-gray-500 italic">Sedang memuat detail properti...</div></Layout>;

  return (
    <Layout>
      <div className="mt-6 px-4">
        {/* Slider Section */}
        <div className="mb-4 rounded-xl overflow-hidden shadow-lg bg-black border border-gray-200">
          <Swiper modules={[Navigation, Pagination]} navigation pagination={{ clickable: true }} className="w-full">
            {property.slides.map((slide, idx) => (
              <SwiperSlide key={idx}>
                {slide.type === 'image' ? (
                  <img src={slide.url} alt={property.title} className="w-full h-64 object-cover" />
                ) : (
                  <iframe src={slide.url} title="Video Property" className="w-full h-64" allowFullScreen />
                )}
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        {/* Content Section */}
        <div className="space-y-2">
          <h1 className="text-2xl font-bold text-gray-900 leading-tight">{property.title}</h1>
          <div className="flex items-center gap-2">
            <span className="text-2xl font-black text-yellow-600">
              {Number(property.price).toFixed(7)}
            </span>
            <span className="text-lg font-bold text-yellow-700">π</span>
          </div>
          <p className="text-gray-500 text-sm flex items-center gap-1">
            <span className="grayscale">📍</span> {property.lokasi}
          </p>
        </div>

        {/* Info Grid */}
        <div className="bg-gray-50 border border-gray-200 p-4 rounded-xl text-sm grid grid-cols-2 gap-3 my-6 shadow-sm">
          <div className="col-span-2 border-b pb-1 mb-1 font-semibold text-gray-400 uppercase text-[10px] tracking-widest">Spesifikasi Properti</div>
          <p><span className="opacity-60">🏠 Tipe:</span> <br/><b>{property.tipe}</b></p>
          <p><span className="opacity-60">📐 Luas:</span> <br/><b>{property.luas_tanah}m² / {property.luas_bangunan}m²</b></p>
          <p><span className="opacity-60">🛏️ Kamar Tidur:</span> <br/><b>{property.jumlah_kamar}</b></p>
          <p><span className="opacity-60">🛁 Kamar Mandi:</span> <br/><b>{property.jumlah_kamar_mandi}</b></p>
        </div>

        {/* Action Button */}
        <button
          onClick={() => isLoggedIn ? setShowModal(true) : navigate('/')}
          className={`w-full py-4 rounded-2xl font-extrabold text-white transition-all shadow-lg active:scale-95 ${
            isLoggedIn 
              ? 'bg-gradient-to-r from-yellow-500 to-yellow-600 hover:shadow-yellow-200' 
              : 'bg-gray-400'
          }`}
        >
          {isLoggedIn ? '💰 BELI SEKARANG' : 'SILAKAN LOGIN DI HOME'}
        </button>
      </div>

      {/* Modal Form Pembeli */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 animate-fadeIn">
          <div className="bg-white rounded-3xl p-6 w-full max-w-md max-h-[85vh] overflow-y-auto shadow-2xl relative">
            <div className="sticky top-0 bg-white pb-4 mb-4 border-b">
               <h2 className="text-xl font-black text-gray-800">Formulir Pengiriman</h2>
               <p className="text-xs text-gray-500">Mohon isi alamat lengkap sesuai KTP/Domisili</p>
            </div>
            
            <div className="space-y-4">
              {Object.keys(buyer).map((key) => (
                <div key={key} className="flex flex-col gap-1">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter ml-1">
                    {key.replace('_', ' ')}
                  </label>
                  <input
                    type="text"
                    placeholder={`Masukkan ${key.replace('_', ' ')}...`}
                    value={buyer[key]}
                    onChange={(e) => setBuyer({ ...buyer, [key]: e.target.value })}
                    className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:bg-white outline-none text-gray-800 transition-all text-sm"
                  />
                </div>
              ))}
            </div>

            <div className="mt-8 space-y-3">
              <button
                onClick={startPiPayment}
                disabled={isPaying}
                className={`w-full py-4 rounded-2xl font-black text-white shadow-xl transition-all ${
                  isPaying ? 'bg-gray-300 animate-pulse' : 'bg-yellow-500 hover:bg-yellow-600'
                }`}
              >
                {isPaying ? 'SEDANG MEMPROSES...' : 'KONFIRMASI & BAYAR'}
              </button>
              <button 
                onClick={() => !isPaying && setShowModal(false)} 
                className="w-full text-sm font-bold text-gray-400 py-2"
              >
                BATALKAN
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
